import * as pdfjs from 'pdfjs-dist/legacy/build/pdf';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import {
  ImportParseResult,
  ImportRow,
  makeImportId,
  normalizeDate,
  parseAmount,
} from './types';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

type TextItem = { x: number; y: number; str: string; page: number };

const DATE_RE = /^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/;
const MONEY_RE = /^\d{1,3}(,\d{3})*(\.\d{2})$/;
const SKIP_RE =
  /OPENING\s+BALANCE|CLOSING\s+BALANCE|STATEMENT\s+OPENING|STATEMENT\s+CLOSING|END OF STATEMENT|UNCOLLECTED|DEBITS\s+\d|CREDITS\s+\d|CR COUNT|DR COUNT/i;

function groupLines(items: TextItem[]): TextItem[][] {
  const pages = new Map<number, TextItem[]>();
  for (const item of items) {
    if (!pages.has(item.page)) pages.set(item.page, []);
    pages.get(item.page)!.push(item);
  }

  const lines: TextItem[][] = [];
  for (const pageItems of pages.values()) {
    const byY = new Map<number, TextItem[]>();
    for (const item of pageItems) {
      const y = Math.round(item.y / 2) * 2;
      if (!byY.has(y)) byY.set(y, []);
      byY.get(y)!.push(item);
    }
    const sorted = Array.from(byY.entries()).sort((a, b) => b[0] - a[0]);
    for (const [, row] of sorted) {
      lines.push(row.sort((a, b) => a.x - b.x));
    }
  }
  return lines;
}

function detectColumns(lines: TextItem[][]): {
  debitX: number;
  creditX: number;
  balanceX: number;
  referenceX: number;
} | null {
  for (const line of lines) {
    const joined = line.map((i) => i.str).join(' ').toUpperCase();
    if (
      joined.includes('DEBITS') &&
      joined.includes('CREDITS') &&
      joined.includes('TRN')
    ) {
      const debit = line.find((i) => /debits?/i.test(i.str));
      const credit = line.find((i) => /credits?/i.test(i.str));
      const balance = line.find((i) => /balance/i.test(i.str));
      const reference = line.find((i) => /references?/i.test(i.str));
      if (debit && credit) {
        return {
          debitX: debit.x,
          creditX: credit.x,
          balanceX: balance?.x ?? credit.x + 90,
          referenceX: reference?.x ?? 229,
        };
      }
    }
  }
  return null;
}

function parseBankLines(
  lines: TextItem[][],
  cols: {
    debitX: number;
    creditX: number;
    balanceX: number;
    referenceX: number;
  }
): ImportRow[] {
  const midDebitCredit = (cols.debitX + cols.creditX) / 2;
  const midCreditBalance = (cols.creditX + cols.balanceX) / 2;
  const rows: ImportRow[] = [];
  let index = 0;

  for (const line of lines) {
    if (!line.length) continue;
    const dateToken = line.find(
      (i) => DATE_RE.test(i.str.trim()) && i.x < 60
    );
    if (!dateToken) continue;

    const text = line.map((i) => i.str).join(' ');
    if (SKIP_RE.test(text)) continue;

    const hasBody = line.some((i) => i.x > 70 && i.x < 350);
    if (!hasBody) continue;

    const date = normalizeDate(dateToken.str.trim());
    if (!date) continue;

    let debitAmt: number | null = null;
    let creditAmt: number | null = null;

    for (const token of line) {
      if (!MONEY_RE.test(token.str.trim())) continue;
      const amt = parseAmount(token.str);
      if (amt == null || amt <= 0) continue;
      if (token.x < midDebitCredit) debitAmt = amt;
      else if (token.x < midCreditBalance) creditAmt = amt;
    }

    let type: 'income' | 'expense' | null = null;
    let amount = 0;
    if (creditAmt && creditAmt > 0) {
      type = 'income';
      amount = creditAmt;
    } else if (debitAmt && debitAmt > 0) {
      type = 'expense';
      amount = debitAmt;
    }
    if (!type || amount <= 0) continue;

    const refTokens = line.filter(
      (i) =>
        i.x >= cols.referenceX - 30 &&
        i.x < cols.debitX - 15 &&
        /[A-Za-z0-9]{8,}/.test(i.str.trim())
    );
    const reference = refTokens.map((i) => i.str.trim()).join(' ') || undefined;

    const descParts = line
      .filter((i) => {
        if (i === dateToken) return false;
        if (i.x >= cols.referenceX - 10) return false;
        if (DATE_RE.test(i.str.trim())) return false;
        return true;
      })
      .map((i) => i.str.trim())
      .filter(Boolean);

    let description = descParts.join(' ').replace(/\s+/g, ' ').trim();
    if (!description) {
      description = type === 'income' ? 'Bank credit' : 'Bank debit';
    }

    rows.push({
      id: makeImportId(index++),
      date,
      type,
      amount,
      description,
      reference,
      wallet: 'bank',
      marked: true,
    });
  }

  return rows;
}

function parseGenericPdfText(fullText: string): ImportRow[] {
  const rows: ImportRow[] = [];
  const lines = fullText
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  let index = 0;

  for (const line of lines) {
    if (SKIP_RE.test(line)) continue;
    const dateMatch = line.match(
      /(\d{1,2}-[A-Za-z]{3}-\d{2,4}|\d{4}-\d{2}-\d{2}|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/
    );
    if (!dateMatch) continue;
    const date = normalizeDate(dateMatch[1]);
    if (!date) continue;

    const amounts = [...line.matchAll(/([\d,]+\.\d{2})/g)].map((m) =>
      parseAmount(m[1])
    );
    const valid = amounts.filter((n): n is number => n != null && n > 0);
    if (!valid.length) continue;

    const amount = valid[0];
    const isIncome = /credit|deposit|salary|interest|remittance/i.test(line);
    rows.push({
      id: makeImportId(index++),
      date,
      type: isIncome ? 'income' : 'expense',
      amount,
      description:
        line
          .replace(dateMatch[0], '')
          .replace(/[\d,]+\.\d{2}/g, '')
          .trim() || 'Imported',
      marked: true,
    });
  }
  return rows;
}

export async function parsePdfFile(file: File): Promise<ImportParseResult> {
  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const allItems: TextItem[] = [];
  const textChunks: string[] = [];

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    for (const item of content.items as Array<{
      str?: string;
      transform?: number[];
    }>) {
      if (!item.str || !item.str.trim() || !item.transform) continue;
      allItems.push({
        x: item.transform[4],
        y: item.transform[5],
        str: item.str,
        page: pageNum,
      });
      textChunks.push(item.str);
    }
    textChunks.push('\n');
  }

  const lines = groupLines(allItems);
  const cols = detectColumns(lines);
  const warnings: string[] = [];
  let rows: ImportRow[] = [];

  if (cols) {
    rows = parseBankLines(lines, cols);
  }

  if (!rows.length) {
    rows = parseGenericPdfText(textChunks.join(' '));
    if (rows.length) {
      warnings.push(
        'Used generic PDF text parsing. Please review types carefully.'
      );
    }
  }

  if (!rows.length) {
    warnings.push(
      'No transactions found in this PDF. Try CSV/Excel, or a clearer bank statement.'
    );
  }

  return { rows, source: 'pdf', warnings };
}
